import React, { useEffect, useMemo, useState } from 'react';
import DOMPurify from "dompurify";
import { Modal, Button } from 'react-bootstrap';
import { FaEdit, FaEye, FaTrash } from 'react-icons/fa';
import EditorDeTexto from './EditorDeTezto';
import { subirINoticia } from '../../firebase/firebase.config';
import './EditarNoticias.css';

const API_BASE = 'https://us-central1-inprojuy-f1173.cloudfunctions.net/app';

const EditarNoticias = () => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeItem, setActiveItem] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const [form, setForm] = useState({
    titulo: '',
    imagenUrl: '',
    contenido: '',
  });
  const [imagenFile, setImagenFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');

  const loadNews = () => {
    setLoading(true);
    fetch(`${API_BASE}/noticias`)
      .then((res) => res.json())
      .then((data) => {
        const sorted = data.sort((a, b) => b.timestamp - a.timestamp);
        setNews(sorted);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    loadNews();
  }, []);

  const normalizeItem = (item) => {
    const docId = item?.docId || item?.id;
    return {
      ...item,
      docId,
      imagenUrl: item?.imagenUrl || item?.imagen || '',
      contenido: item?.contenido || item?.cuerpo || '',
      titulo: item?.titulo || '',
    };
  };

  const openEdit = (item) => {
    const normalized = normalizeItem(item);
    setActiveItem(normalized);
    setForm({
      titulo: normalized.titulo,
      imagenUrl: normalized.imagenUrl,
      contenido: normalized.contenido,
    });
    setImagenFile(null);
    setShowModal(true);
  };

  const closeEdit = () => {
    setShowModal(false);
    setActiveItem(null);
    setImagenFile(null);
  };

  useEffect(() => {
    if (imagenFile) {
      const url = URL.createObjectURL(imagenFile);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreviewUrl(form.imagenUrl || '');
  }, [imagenFile, form.imagenUrl]);

  const handleImagenChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setImagenFile(file);
    }
  };

  const handleSave = async () => {
    const docId = activeItem?.docId || activeItem?.id;
    if (!docId) return;
    setIsSaving(true);
    try {
      const payload = {};
      let uploaded = null;
      if (imagenFile) {
        uploaded = await subirINoticia(imagenFile);
        payload.imagenUrl = uploaded.url;
        payload.imagen = uploaded.url;
        if (uploaded.imageName) payload.nombreimagen = uploaded.imageName;
      }
      if (form.titulo !== activeItem.titulo) payload.titulo = form.titulo;
      if (form.contenido !== (activeItem.contenido || activeItem.cuerpo)) {
        payload.contenido = form.contenido;
        payload.cuerpo = form.contenido;
      }
      if (Object.keys(payload).length > 0) {
        payload.timestamp = Date.now();
      }

      if (Object.keys(payload).length === 0) {
        setIsSaving(false);
        return;
      }

      const response = await fetch(`${API_BASE}/noticias/${docId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const text = await response.text();
        alert(`No se pudo guardar (${response.status}). ${text}`);
        return;
      }
      closeEdit();
      loadNews();
    } catch (e) {
      alert('Error al guardar los cambios.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (item) => {
    const deleteKey = item?.timestamp ?? item?.id;
    if (!deleteKey) return;
    if (!window.confirm('¿Eliminar esta noticia?')) return;
    setIsDeleting(true);
    try {
      await fetch(`${API_BASE}/noticiasdel/${deleteKey}`, {
        method: 'DELETE',
      });
      loadNews();
    } catch (e) {
      // noop
    } finally {
      setIsDeleting(false);
    }
  };

  const previewHtml = useMemo(
    () => previewItem?.contenido || '<p>(Sin contenido)</p>',
    [previewItem?.contenido]
  );

  const formatFecha = (value) => {
    if (!value) return 'Sin fecha';
    const date = new Date(Number(value));
    if (Number.isNaN(date.getTime())) return 'Sin fecha';
    return date.toLocaleString('es-AR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="editar-noticias">
      <div className="editar-noticias__header">
        <h3>Editar Noticias</h3>
        <p className="text-muted">Seleccioná una noticia para editar o eliminar.</p>
      </div>

      {loading ? (
        <div className="text-muted">Cargando noticias...</div>
      ) : (
        <div className="editar-noticias__list">
          {news.map((item) => (
            <div key={item.docId || item.id} className="editar-noticias__item">
              <img src={item.imagenUrl || item.imagen} alt={item.titulo} className="editar-noticias__thumb" />
              <div className="editar-noticias__info">
                <div className="editar-noticias__title">{item.titulo}</div>
                <div className="editar-noticias__meta">
                  Última actualización: {formatFecha(item.timestamp || item.id)}
                </div>
              </div>
              <div className="editar-noticias__actions">
                <button
                  className="btn btn-outline-secondary btn-sm"
                  onClick={() => {
                    const normalized = normalizeItem(item);
                    setPreviewItem(normalized);
                    setShowPreview(true);
                  }}
                >
                  <FaEye /> Ver
                </button>
                <button className="btn btn-outline-primary btn-sm" onClick={() => openEdit(item)}>
                  <FaEdit /> Editar
                </button>
                <button
                  className="btn btn-outline-danger btn-sm"
                  onClick={() => handleDelete(item)}
                  disabled={isDeleting}
                >
                  <FaTrash /> Eliminar
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Modal show={showModal} onHide={closeEdit} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Editar noticia</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">Título</label>
              <input
                className="form-control"
                value={form.titulo}
                onChange={(e) => setForm({ ...form, titulo: e.target.value })}
              />
            </div>
            <div className="col-md-6">
              <label className="form-label">Imagen actual</label>
              <div className="editar-noticias__image-box">
                {previewUrl ? (
                  <img src={previewUrl} alt="Imagen actual" />
                ) : (
                  <div className="editar-noticias__image-empty">Sin imagen</div>
                )}
              </div>
              <div className="mt-2">
                <label className="form-label">Reemplazar imagen</label>
                <input
                  type="file"
                  accept="image/jpeg, image/png"
                  className="form-control"
                  onChange={handleImagenChange}
                />
              </div>
            </div>
            <div className="col-12">
              <label className="form-label">Contenido</label>
              <EditorDeTexto onChange={(v) => setForm({ ...form, contenido: v })} value={form.contenido} />
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeEdit}>
            Cerrar
          </Button>
          <Button variant="primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Guardando...' : 'Guardar cambios'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showPreview} onHide={() => setShowPreview(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Previsualización</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="editar-noticias__preview-modal">
            <h3 className="mb-3">{previewItem?.titulo || 'Título de la noticia'}</h3>
            {previewItem?.imagenUrl && (
              <div className="editar-noticias__preview-modal-image">
                <img src={previewItem.imagenUrl} alt="Imagen de la noticia" />
              </div>
            )}
            <div
              className="editar-noticias__preview"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(previewHtml) }}
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowPreview(false)}>
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default EditarNoticias;
